// eslint-disable import/prefer-default-export
import { casingFormat, formatName } from 'common/utils';
import { capitalizeName } from 'common/utils/titleCase';
import { filter, find, includes, map, omit, reduce, toLower, trim } from 'lodash';

import { placeholderEmailRegex } from 'common/constants/regex';

export const transformGetPatients = ({ getPatients }) => {
  const transFormedPatientData = {
    count: getPatients.count,
    patients: map(getPatients.results, ({ patientDemographics, patientContact }) => ({
      ...patientDemographics,
      firstName: casingFormat(formatName(patientDemographics.firstName)),
      lastName: casingFormat(formatName(patientDemographics.lastName)),
      patientName: `${casingFormat(formatName(patientDemographics.lastName))}, ${casingFormat(
        formatName(patientDemographics.firstName)
      )}`,
      ...patientContact,
      emails: map(patientContact.emails, (email) => ({
        ...email,
        email: toLower(email.email),
      })),
    })),
  };

  return transFormedPatientData;
};

export const transformListPatientComments = ({ listPatientComments }) => {
  return map(listPatientComments, 'comment');
};

export const transformPatientAddress = (address) => {
  return {
    ...omit(address, ['tags']),
    isDefault: includes(address.tags, 'DEFAULT'),
  };
};

export const transformPatientAddresses = ({ addresses }) => {
  return map(addresses, transformPatientAddress);
};

const compareByPartnerName = (first, second) =>
  first.partnerName.localeCompare(second.partnerName, undefined, { sensitivity: 'base' });

const transformPatientContactFromPartner = ({
  partnerId,
  partnerName,
  patientEmail,
  patientPhoneNumber,
}) => ({
  partnerId,
  partnerName: capitalizeName(partnerName || partnerId),
  email: patientEmail ? toLower(patientEmail) : undefined,
  phoneNumber: patientPhoneNumber || undefined,
  hasPlaceholderEmail: placeholderEmailRegex.test(trim(patientEmail)),
});

// The email and phone each partner gave for this patient, from getPatient or
// setPatientContactsForPartners. A partner with neither is kept so its contact can be entered again.
export const transformPatientContactsFromPartners = (patientContactsFromPartners) =>
  (patientContactsFromPartners || [])
    .map(transformPatientContactFromPartner)
    .sort(compareByPartnerName);

export const transformPatientData = ({ getPatient }) => {
  const { patientContact, patientContactsFromPartners, patientDemographics, patientIdentity, mpi } =
    getPatient;
  const firstName = casingFormat(formatName(patientDemographics.firstName));
  const lastName = casingFormat(formatName(patientDemographics.lastName));
  const middleName =
    patientDemographics?.middleName && casingFormat(formatName(patientDemographics.middleName));
  const patientName = `${lastName}, ${firstName}`;

  return {
    ...omit(patientDemographics, 'preferencesState'),
    firstName,
    lastName,
    middleName,
    patientName,
    mpi,
    systemIds: omit(patientIdentity, 'partnerIdentities'),
    partnerIds: patientIdentity.partnerIdentities,
    email: toLower(find(patientContact?.emails, { tags: ['DEFAULT'] })?.email),
    mobilePhone: find(patientContact.phoneNumbers, { tags: ['MOBILE'] })?.phoneNumber,
    workPhone: find(patientContact.phoneNumbers, { tags: ['WORK'] })?.phoneNumber,
    homePhone: find(patientContact.phoneNumbers, { tags: ['HOME'] })?.phoneNumber,
    contactsFromPartners: transformPatientContactsFromPartners(patientContactsFromPartners),
    addresses: transformPatientAddresses({ addresses: patientContact.addresses || [] }),
    notificationPreferences: patientDemographics.preferencesState,
    isMobilePhoneVerified: find(patientContact.phoneNumbers, { tags: ['MOBILE'] })?.isVerified,
  };
};

export const transformFullPatientDetails = (response) => {
  // Response is an array like this [{data: {getMedication : value}}, {data: {getGuarantors : value}}]
  // responseObj is simply transforming the array into {getMedication: value, getGuarantors: value} for easier consumption
  const responseObj = reduce(
    map(response, 'data'),
    (result, value) => {
      return { ...result, ...value };
    },
    {}
  );

  return {
    guarantors: responseObj.getGuarantors,
    insurances: responseObj.getPatientInsurances,
    medications: responseObj.listPatientMedications.results,
    authorization: responseObj.getAuthorizations?.authorization || null,
  };
};

const transformTransferRequestList = (transferRequestList, requestId) => {
  return reduce(
    transferRequestList,
    (
      results,
      {
        taggedForReview,
        status,
        requestLineItemId,
        needsByDate,
        rxNumber,
        item,
        reviewReason,
        hasPinnedNote,
      }
    ) => ({
      ...results,
      [requestLineItemId]: {
        ...item.patientMedication,
        requestLineItemId,
        status,
        needsByDate,
        rxNumber,
        requestId,
        taggedForReview,
        reviewReason,
        hasPinnedNote,
      },
    }),
    {}
  );
};

const transformListPatientRequests = ({ listPatientRequests }) =>
  reduce(
    listPatientRequests,
    (results, { requestLineItems, ...patientRequest }) =>
      patientRequest.status === 'OPEN'
        ? {
            ...results,
            [patientRequest.requestId]: {
              ...patientRequest,
              transferRequest: transformTransferRequestList(
                filter(requestLineItems, { itemType: 'TRANSFER_REQUEST' }),
                patientRequest.requestId
              ),
            },
          }
        : results,
    {}
  );

export const transformListFailedAdjudications = ({ listAdjudicationRejectedPrescriptions }) => ({
  count: listAdjudicationRejectedPrescriptions.count,
  results: listAdjudicationRejectedPrescriptions.results,
});

export const transformGetPatientActionItems = (response) => {
  const { listPatientRequests, listPatientOrders } = reduce(
    map(response, 'data'),
    (result, value) => {
      return { ...result, ...value };
    },
    {}
  );

  return {
    requests: transformListPatientRequests({ listPatientRequests }),
    orders: listPatientOrders.results,
  };
};

export const transformNote = ({ comment }) => ({
  noteId: comment.commentId,
  noteContent: comment.commentContent.message,
  noteLineItemId: comment.commentContext.id,
  noteState: comment.currentState.state,
  noteCreatedAt: comment.createdAt,
  noteAuthor: comment.author,
  isShippingRelated: comment.commentContent.isShippingRelated ?? false,
});

export const transformLineItemNotes = (notes) => map(notes, transformNote);

export const transformGetPatientUsers = ({ getPatientUsers: patientUsers }) => {
  return map(patientUsers, ({ peUser: { userId, firstName, lastName, emailAddress } }) => {
    return { userId, userName: `${lastName}, ${firstName}`, email: emailAddress };
  });
};

export const transformGuarantorData = ({ getGuarantors: guarantors }) => [
  ...map(guarantors, ({ firstName, lastName, ...otherDetails }) => ({
    ...otherDetails,
    firstName: casingFormat(formatName(firstName)),
    lastName: casingFormat(formatName(lastName)),
  })),
];
